import React, { useRef, useEffect, useState } from "react";
import { useContent } from "../context/ContentContext";
import FormatToolbar from "./FormatToolbar";

/**
 * Inline click-to-edit component.
 * - When not in edit mode (or not admin) it just renders the HTML.
 * - When in edit mode it becomes contentEditable; on blur it saves innerHTML.
 *
 * Supports rich text (bold/italic/underline) because it stores raw HTML.
 */
export default function Editable({
  html,
  onChange,
  as: Tag = "div",
  className = "",
  style = {},
  placeholder = "",
  singleLine = false,
}) {
  const { editMode, isAdmin } = useContent();
  const ref = useRef(null);
  const [focused, setFocused] = useState(false);
  const editing = editMode && isAdmin;

  // Keep DOM in sync with incoming html when NOT focused
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (html || "")) {
      if (document.activeElement !== ref.current) {
        ref.current.innerHTML = html || "";
      }
    }
  }, [html]);

  const handleBlur = () => {
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const handleKeyDown = (e) => {
    if (singleLine && e.key === "Enter") {
      e.preventDefault();
      ref.current && ref.current.blur();
    }
  };

  if (!editing) {
    return (
      <Tag
        className={`rich-content ${className}`}
        style={style}
        dangerouslySetInnerHTML={{ __html: html || "" }}
      />
    );
  }

  return (
    <>
      <Tag
        ref={ref}
        className={className}
        style={style}
        data-editable="true"
        contentEditable
        suppressContentEditableWarning
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          if (e.relatedTarget && e.relatedTarget.closest("[data-format-toolbar]")) return;
          setFocused(false);
          handleBlur();
        }}
        onKeyDown={handleKeyDown}
        data-placeholder={placeholder}
        dangerouslySetInnerHTML={{ __html: html || "" }}
      />
      {focused && (
        <FormatToolbar
          targetRef={ref}
          onDone={() => {
            setFocused(false);
            handleBlur();
          }}
        />
      )}
    </>
  );
}
